// src/scene-canvas/runtime/layout/resize.ts

// D: Declaration code and E: execution code
import type { PLike } from "../p/makeP";          // (D) type-only import (erased at runtime)
import type { DprMode } from "./viewport";       // (D) type-only import (erased at runtime)
import { resolvePixelDensity } from "./viewport"; // (D+E*) import binding declared; module loader may execute that module once
import { applyCanvasStyle } from "./mount";       // (D+E*) same note as above

export type ResizeCleanup = () => void; // (D) type alias only

export function installResizeHandlers(opts: {
  parentEl: HTMLElement;
  canvasEl: HTMLCanvasElement;
  p: PLike;
  dprMode: DprMode;
  resizeTo: () => { w: number; h: number };
  onAfterResize?: () => void;
}): ResizeCleanup {
  const { parentEl, canvasEl, p, dprMode, resizeTo, onAfterResize } = opts;

  let resizeRaf: number | null = null;
  let lastAppliedW = -1;
  let lastAppliedH = -1;
  let lastAppliedDpr = -1;

  function resizeNow() {
    const { w, h } = resizeTo();
    const nextDpr = resolvePixelDensity(dprMode);

    if (w === lastAppliedW && h === lastAppliedH && nextDpr === lastAppliedDpr) {
      return;
    }

    lastAppliedW = w;
    lastAppliedH = h;
    lastAppliedDpr = nextDpr;

    p.pixelDensity(nextDpr);
    p.resizeCanvas(w, h);

    canvasEl.style.width = `${String(w)}px`;
    canvasEl.style.height = `${String(h)}px`;

    applyCanvasStyle(canvasEl);

    onAfterResize?.();
  }

  function resizeThrottled() {
    if (resizeRaf != null) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(resizeNow);
  }

  // Observe parent size changes
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => { resizeThrottled(); });
    ro.observe(parentEl);
  }

  // Window resize
  window.addEventListener("resize", resizeThrottled);

  // Visibility
  const visHandler = () => {
    if (document.visibilityState === "visible") resizeThrottled();
  };
  document.addEventListener("visibilitychange", visHandler);

  // initial
  resizeNow();

  return () => {
    try {
      if (resizeRaf != null) cancelAnimationFrame(resizeRaf);
    } catch {}
    try {
      ro?.disconnect();
    } catch {}
    ro = null;

    try {
      window.removeEventListener("resize", resizeThrottled);
    } catch {}
    try {
      document.removeEventListener("visibilitychange", visHandler);
    } catch {}
  };
}
